class IRNewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.modal = this.querySelector('.ir-popup-modal');
    this.closeBtn = this.querySelector('.ir-popup-close');
    this.expirationHours = parseInt(this.dataset.expiration || '24', 10);
    this.delay = parseInt(this.dataset.delay || '3000', 10);
    this.form = this.querySelector('form');

    this.STORAGE_KEY = 'ironrise_popup_dismissed';

    // Event Listeners for closing
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }
    
    // Close on overlay click
    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
    });

    // Close newsletter popup if cart drawer or any other cart modal opens
    document.addEventListener('dialog:open', (e) => {
      if (e.target && e.target.closest && e.target.closest('cart-drawer-component')) {
        if (this.hasAttribute('open')) {
          this.close(true);
        }
      }
    });

    // Theme Editor integration
    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:select', (ev) => {
        const section = this.closest('.shopify-section');
        const sectionId = section && section instanceof HTMLElement ? section.dataset.id : '';
        const detail = ev instanceof CustomEvent ? ev.detail : null;
        if ((detail && typeof detail.sectionId === 'string' && detail.sectionId.includes(sectionId)) || (ev.target instanceof Node && this.contains(ev.target))) {
          this.open();
        }
      });
      document.addEventListener('shopify:section:deselect', (ev) => {
        const section = this.closest('.shopify-section');
        const sectionId = section && section instanceof HTMLElement ? section.dataset.id : '';
        const detail = ev instanceof CustomEvent ? ev.detail : null;
        if ((detail && typeof detail.sectionId === 'string' && detail.sectionId.includes(sectionId)) || (ev.target instanceof Node && this.contains(ev.target))) {
          this.close(true); // close without saving dismissal
        }
      });
      // Don't auto-popup inside editor until selected
      return; 
    }

    if (this.isCartPageOrDrawerOpen()) {
      return;
    }

    // Show success message and dismiss after 5 seconds
    if (this.querySelector('.ir-popup-success')) {
      this.setDismissed();
      this.open();
      setTimeout(() => {
        this.close();
      }, 5000);
      return;
    }

    // Set storage if user submits
    const form = this.form;
    if(form) {
      form.addEventListener('submit', () => {
         this.setDismissed();
         const submitBtn = form.querySelector('button[type="submit"]');
         if (submitBtn) {
           submitBtn.textContent = 'Submitting...';
         }
      });
    }

    this.init();
  }

  disconnectedCallback() {
    this.#removeScrollListener();
  }

  isCartPageOrDrawerOpen() {
    if (window.location.pathname.includes('/cart')) {
      return true;
    }
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      const dialog = cartDrawer.querySelector('dialog[open], .cart-drawer__dialog[open]');
      if (dialog) return true;
    }
    return false;
  }

  init() {
    if (this.isCartPageOrDrawerOpen()) {
      return;
    }

    const dismissData = localStorage.getItem(this.STORAGE_KEY);
    if (dismissData) {
      const dismissTime = parseInt(dismissData, 10);
      const now = new Date().getTime();
      const expirationMs = this.expirationHours * 60 * 60 * 1000;

      if (now - dismissTime < expirationMs) {
        this.remove(); // Destroy DOM element completely since cooling down
        return; // Still hidden
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    }

    const trigger = this.dataset.trigger || 'delay';

    if (trigger === 'immediately') {
      this.open();
    } else if (trigger === 'scroll') {
      this.#setupScrollTrigger();
    } else {
      // timed delay
      setTimeout(() => {
        if (!this.isCartPageOrDrawerOpen()) {
          this.open();
        }
      }, this.delay);
    }
  }

  #scrollHandler = null;

  #setupScrollTrigger() {
    this.#scrollHandler = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 100;

      if (scrollPercent >= 50) {
        this.#removeScrollListener();
        if (!this.isCartPageOrDrawerOpen()) {
          this.open();
        }
      }
    };

    window.addEventListener('scroll', this.#scrollHandler, { passive: true });
    // Check immediately in case page loaded already scrolled past 50%
    this.#scrollHandler();
  }

  #removeScrollListener() {
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }
  }

  open() {
    if (this.isCartPageOrDrawerOpen()) {
      return;
    }
    this.#removeScrollListener();
    this.removeAttribute('hidden');
    // Force browser reflow
    this.offsetHeight;
    this.setAttribute('open', '');
  }

  close(ignoreDismiss = false) {
    this.#removeScrollListener();
    if (!ignoreDismiss) {
      this.setDismissed();
    }
    this.removeAttribute('open');
    // Hide DOM element fully after 400ms CSS animation
    setTimeout(() => {
      this.setAttribute('hidden', '');
    }, 400); 
  }

  setDismissed() {
    localStorage.setItem(this.STORAGE_KEY, new Date().getTime().toString());
  }
}

if (!customElements.get('ir-newsletter-popup')) {
  customElements.define('ir-newsletter-popup', IRNewsletterPopup);
}
